import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, message, Space, Spin, Modal, Tag, Card } from 'antd'
import { ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons'
import TaskForm from '../components/Task/TaskForm'
import PaymentCheckout from '../components/Payment/PaymentCheckout'
import {
  startAnalysis,
  setCurrentTaskId,
  fetchBillingSummary,
  fetchTaskQuote,
  fetchTask,
  clearPaymentState,
  retryTaskPayment,
} from '../store/slices/taskSlice'
import './TaskNew.css'

const decodeSupportContext = (value) => {
  if (!value) return null
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
    const padLength = (4 - (normalized.length % 4)) % 4
    const padded = normalized + '='.repeat(padLength)
    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
      return window.atob(padded)
    }
    return null
  } catch (error) {
    console.warn('Failed to decode support context', error)
    return null
  }
}

const PENDING_PAYMENT_STORAGE_KEY = 'pendingPaymentTask'

const TaskNew = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const {
    submitting,
    currentTaskId,
    error,
    paymentRequired,
    paymentClientSecret,
    pendingPaymentTaskId,
    currentTask,
    billingSummary,
    billingLoading,
    quote,
    quoteLoading,
  } = useSelector((state) => state.task)

  const isSubmittingRef = useRef(false)
  const quoteTimerRef = useRef(null)
  const lastQuoteRef = useRef(null)
  const paymentAcknowledgedRef = useRef(false)

  const [waitingForPaymentDetails, setWaitingForPaymentDetails] = useState(false)
  const [waitingStartTime, setWaitingStartTime] = useState(null)
  const [waitingElapsed, setWaitingElapsed] = useState(0)
  const [formValues, setFormValues] = useState(null)
  const [rehydratingPayment, setRehydratingPayment] = useState(false)
  const [prefill, setPrefill] = useState({})
  const [prefillContext, setPrefillContext] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tickerParam = params.get('ticker')
    const contextParam = params.get('context')

    const nextPrefill = {}
    if (tickerParam) {
      nextPrefill.ticker = tickerParam.toUpperCase()
    }
    setPrefill(nextPrefill)
    setPrefillContext(contextParam ? decodeSupportContext(contextParam) : null)
  }, [location.search])

  const billingAmount = currentTask?.billingAmount
    ?? currentTask?.billing_amount
    ?? quote?.totalAmount
    ?? quote?.total_amount
    ?? null
  const billingCurrency = currentTask?.billingCurrency
    ?? currentTask?.billing_currency
    ?? billingSummary?.currency
    ?? 'USD'
  const researchDepthValue = currentTask?.researchDepth
    ?? currentTask?.research_depth
    ?? quote?.researchDepth
    ?? quote?.research_depth
    ?? formValues?.researchDepth
    ?? null
  const analystCountValue = currentTask?.analystCount
    ?? currentTask?.analyst_count
    ?? quote?.analystCount
    ?? quote?.analyst_count
    ?? formValues?.selectedAnalysts?.length
    ?? null

  const showPaymentStep = Boolean(paymentRequired && pendingPaymentTaskId)
  const paymentTaskId = useMemo(() => (
    pendingPaymentTaskId
    ?? currentTask?.taskId
    ?? currentTask?.task_id
    ?? null
  ), [pendingPaymentTaskId, currentTask])

  // Clear task context when entering the page
  useEffect(() => {
    let isMounted = true

    const rehydratePendingPayment = async () => {
      const stored = typeof window !== 'undefined'
        ? window.localStorage.getItem(PENDING_PAYMENT_STORAGE_KEY)
        : null

      if (!stored) {
        dispatch(clearPaymentState())
        return
      }

      let parsedTaskId = null
      try {
        const parsed = JSON.parse(stored)
        parsedTaskId = parsed?.taskId || null
      } catch (err) {
        console.error('Failed to parse pending payment state:', err)
      }

      if (!parsedTaskId) {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY)
        }
        dispatch(clearPaymentState())
        return
      }

      setRehydratingPayment(true)
      isSubmittingRef.current = false

      try {
        const taskPayload = await dispatch(fetchTask(parsedTaskId)).unwrap()
        const requiresPayment = Boolean(taskPayload?.paymentRequired || taskPayload?.payment_required)
        const payloadSecret = taskPayload?.paymentClientSecret || taskPayload?.payment_client_secret

        if (requiresPayment && !payloadSecret) {
          await dispatch(retryTaskPayment(parsedTaskId)).unwrap()
        }

        if (requiresPayment) {
          paymentAcknowledgedRef.current = true
        } else if (typeof window !== 'undefined') {
          window.localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY)
        }
      } catch (err) {
        console.error('Failed to rehydrate pending payment state:', err)
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY)
        }
        dispatch(clearPaymentState())
      } finally {
        if (isMounted) {
          setRehydratingPayment(false)
        }
      }
    }

    rehydratePendingPayment()
    dispatch(setCurrentTaskId(null))
    dispatch(fetchBillingSummary())

    return () => {
      isMounted = false
    }
  }, [dispatch])

  // Only navigate when a free task was submitted
  useEffect(() => {
    if (currentTaskId && isSubmittingRef.current) {
      isSubmittingRef.current = false
      message.success('Analysis started successfully!')
      navigate(`/tasks/${currentTaskId}`)
    }
  }, [currentTaskId, navigate])

  useEffect(() => {
    if (error) {
      message.error(error)
      isSubmittingRef.current = false
    }
  }, [error])

  useEffect(() => {
    if (!formValues) {
      return
    }
    const { researchDepth, selectedAnalysts } = formValues
    if (!researchDepth || !selectedAnalysts || selectedAnalysts.length === 0) {
      return
    }

    const requestPayload = {
      researchDepth,
      selectedAnalysts,
    }

    if (lastQuoteRef.current
      && lastQuoteRef.current.researchDepth === requestPayload.researchDepth
      && JSON.stringify(lastQuoteRef.current.selectedAnalysts) === JSON.stringify(requestPayload.selectedAnalysts)
    ) {
      return
    }

    if (quoteTimerRef.current) {
      clearTimeout(quoteTimerRef.current)
    }

    quoteTimerRef.current = setTimeout(() => {
      lastQuoteRef.current = requestPayload
      dispatch(fetchTaskQuote(requestPayload))
    }, 350)

    return () => {
      if (quoteTimerRef.current) {
        clearTimeout(quoteTimerRef.current)
      }
    }
  }, [formValues, dispatch])

  useEffect(() => {
    if (!showPaymentStep) {
      setWaitingForPaymentDetails(false)
      paymentAcknowledgedRef.current = false
      if (!rehydratingPayment && typeof window !== 'undefined') {
        window.localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY)
      }
      return
    }

    isSubmittingRef.current = false

    if (!paymentClientSecret) {
      setWaitingForPaymentDetails(true)
    } else {
      setWaitingForPaymentDetails(false)
    }

    if (typeof window !== 'undefined' && pendingPaymentTaskId) {
      window.localStorage.setItem(
        PENDING_PAYMENT_STORAGE_KEY,
        JSON.stringify({ taskId: pendingPaymentTaskId })
      )
    }
  }, [showPaymentStep, paymentClientSecret, pendingPaymentTaskId, rehydratingPayment])

  // Timer for loading state
  useEffect(() => {
    if (waitingForPaymentDetails && !waitingStartTime) {
      setWaitingStartTime(Date.now())
    }
    if (!waitingForPaymentDetails && waitingStartTime) {
      setWaitingStartTime(null)
      setWaitingElapsed(0)
    }
  }, [waitingForPaymentDetails, waitingStartTime])

  useEffect(() => {
    if (!waitingStartTime) {
      return
    }

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - waitingStartTime) / 1000)
      setWaitingElapsed(elapsed)

      if (elapsed === 10) {
        message.warning('Payment preparation is taking longer than expected. Please wait...')
      }

      if (elapsed === 30) {
        message.error('Payment preparation timeout. Please try again or contact support.')
        setWaitingForPaymentDetails(false)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [waitingStartTime])

  const handleSubmit = async (taskData) => {
    isSubmittingRef.current = true
    await dispatch(startAnalysis(taskData))
    dispatch(fetchBillingSummary())
  }

  const handleFormChange = useCallback((values) => {
    if (!values) {
      return
    }
    const normalized = {
      researchDepth: values.researchDepth,
      selectedAnalysts: values.selectedAnalysts || [],
    }
    setFormValues(normalized)
  }, [])

  const handlePaymentSuccess = useCallback(async () => {
    const taskId = pendingPaymentTaskId
    isSubmittingRef.current = false
    message.success('Payment successful! Your analysis will start shortly.')

    if (taskId) {
      await dispatch(fetchTask(taskId))
      navigate(`/tasks/${taskId}`)
    }

    dispatch(clearPaymentState())
    dispatch(fetchBillingSummary())
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY)
    }
  }, [dispatch, navigate, pendingPaymentTaskId])

  const handlePaymentCancel = useCallback(() => {
    const taskId = pendingPaymentTaskId

    Modal.confirm({
      title: 'Cancel payment?',
      content: (
        <div className="task-new-cancel-modal">
          <div className="task-new-cancel-modal__badge">
            <ExclamationCircleOutlined />
          </div>
          <div className="task-new-cancel-modal__content">
            <h3>Payment is still pending</h3>
            <p>Your analysis task is ready, but the checkout is unfinished.</p>
            <div className="task-new-cancel-modal__list">
              <span>What happens next?</span>
              <ul>
                <li>You can finish payment anytime from the task details page.</li>
                <li>The analysis will start automatically after payment succeeds.</li>
                <li>The task remains in "Awaiting Payment" until you confirm.</li>
              </ul>
            </div>
          </div>
        </div>
      ),
      okText: 'Go to task details',
      cancelText: 'Stay here',
      className: 'task-new-cancel-modal__container',
      icon: null,
      width: 520,
      onOk: () => {
        isSubmittingRef.current = false
        dispatch(clearPaymentState())
        if (taskId) {
          navigate(`/tasks/${taskId}`)
        }
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY)
        }
      },
      onCancel: () => {},
    })
  }, [dispatch, navigate, pendingPaymentTaskId])

  useEffect(() => () => {
    if (quoteTimerRef.current) {
      clearTimeout(quoteTimerRef.current)
    }
    lastQuoteRef.current = null
    dispatch(clearPaymentState())
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY)
    }
  }, [dispatch])

  return (
    <div className="task-new-page">
      <div className="task-new-container">
        {waitingForPaymentDetails && (
          <Alert
            className="task-new-alert"
            type={waitingElapsed >= 10 ? 'warning' : 'info'}
            showIcon
            icon={<ClockCircleOutlined />}
            message={(
              <Space size={8}>
                <span>Preparing payment details...</span>
                <Tag color={waitingElapsed >= 10 ? 'orange' : 'blue'}>
                  {waitingElapsed}s elapsed
                </Tag>
              </Space>
            )}
            description={(
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <div className="task-new-alert-row">
                  <Spin size="small" />
                  <span>
                    {waitingElapsed < 5 && 'The system is retrieving payment information for this analysis.'}
                    {waitingElapsed >= 5 && waitingElapsed < 10 && 'Calculating your payment amount and preparing checkout...'}
                    {waitingElapsed >= 10 && waitingElapsed < 20 && 'This is taking longer than expected. Please wait a moment...'}
                    {waitingElapsed >= 20 && 'Almost there! Finalizing payment setup...'}
                  </span>
                </div>
                {waitingElapsed >= 10 && (
                  <div className="task-new-alert-note">
                    Expected time: 3-5 seconds. If this continues, please contact support.
                  </div>
                )}
              </Space>
            )}
          />
        )}

        {!waitingForPaymentDetails && prefillContext && (
          <Alert
            className="task-new-alert"
            type="info"
            showIcon
            message="Pre-filled from AI Support"
            description={prefillContext}
          />
        )}

        <div className="task-new-panels">
          {!showPaymentStep && !rehydratingPayment && (
            <div className="task-new-panel">
              <TaskForm
                onSubmit={handleSubmit}
                loading={submitting}
                billingSummary={billingSummary}
                billingLoading={billingLoading}
                quote={quote}
                quoteLoading={quoteLoading}
                onFormChange={handleFormChange}
                prefill={prefill}
              />
            </div>
          )}

          {(showPaymentStep || rehydratingPayment) && (
            <div className="task-new-panel task-new-panel--payment">
              <Card
                bordered={false}
                className="task-new-payment-card"
              >
                <div className="task-new-payment-header">
                  <h2>Complete Payment</h2>
                  <p>
                    Task
                    {paymentTaskId ? ` #${paymentTaskId}` : ''}
                    {' '}
                    is ready. Complete the secure checkout below to start the analysis.
                  </p>
                </div>

                <PaymentCheckout
                  clientSecret={paymentClientSecret}
                  taskId={pendingPaymentTaskId}
                  amount={billingAmount}
                  currency={billingCurrency}
                  researchDepth={researchDepthValue}
                  analystCount={analystCountValue}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                  submitLabel="Confirm Payment & Start"
                  emptyState={(
                    <div className="task-new-payment-loading">
                      <Spin size="large" />
                      <span>Preparing secure checkout...</span>
                    </div>
                  )}
                />

                <div className="task-new-payment-footer">
                  <span>Payments are processed securely by Stripe.</span>
                  <span>If you cancel payment or leave this page, you can finish payment from the task details view.</span>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TaskNew
